import { FixedSizeList as List } from 'react-window';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';

// -----------------------------

// Memorized Chip
const MemorizedUserChip = ({ user, isSelected, onClick }) => {
  const roleName = user.Role?.Name || 'Unknown';
  const fullName =
    `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.Email || 'Unknown User';

  return (
    <Chip
      label={
        <Stack direction="column" spacing={0.3} alignItems="flex-start">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Iconify icon="mdi:account" width={14} height={14} />
            <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{fullName}</Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {user.Email}
          </Typography>
        </Stack>
      }
      variant={isSelected ? 'filled' : 'outlined'}
      color={isSelected ? 'primary' : 'default'}
      onClick={onClick}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        py: 0.5,
        minHeight: 48,
        '& .MuiChip-label': { width: '100%', display: 'block', px: 1, py: 0.5 },
        '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' },
      }}
    />
  );
};

// -----------------------------
// NotificationUserSelector Component
export const NotificationUserSelector = ({ users = [], selectedUsers, setSelectedUsers }) => {
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Extract unique roles
  const roles = useMemo(() => {
    const setRoles = new Set(users.map((u) => u.Role?.Name).filter(Boolean));
    return Array.from(setRoles).sort();
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (roleFilter !== 'all') filtered = filtered.filter((u) => u.Role?.Name === roleFilter);
    if (debouncedSearch.trim()) {
      const search = debouncedSearch.toLowerCase();
      filtered = filtered.filter((u) => {
        const fullName = `${u.FirstName || ''} ${u.LastName || ''}`.toLowerCase();
        return fullName.includes(search) || (u.Email || '').toLowerCase().includes(search);
      });
    }
    return filtered;
  }, [users, roleFilter, debouncedSearch]);

  const selectedSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);
  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedSet.has(u.Id));

  const handleUserToggle = useCallback(
    (id) => {
      setSelectedUsers((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    },
    [setSelectedUsers]
  );

  const handleSelectAll = useCallback(() => {
    const filteredIds = filteredUsers.map((u) => u.Id);
    if (allFilteredSelected) {
      setSelectedUsers((prev) => prev.filter((i) => !filteredIds.includes(i)));
    } else {
      setSelectedUsers((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  }, [filteredUsers, allFilteredSelected, setSelectedUsers]);

  return (
    <Box>
      {/* Role Filter */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Chip
          label={`All (${users.length})`}
          variant={roleFilter === 'all' ? 'filled' : 'outlined'}
          color={roleFilter === 'all' ? 'primary' : 'default'}
          onClick={() => setRoleFilter('all')}
        />
        {roles.map((role) => {
          const count = users.filter((u) => u.Role?.Name === role).length;
          const selectedCount = users.filter(
            (u) => u.Role?.Name === role && selectedSet.has(u.Id)
          ).length;

          return (
            <Chip
              key={role}
              label={`${role} (${selectedCount}/${count})`}
              variant={roleFilter === role ? 'filled' : 'outlined'}
              color={roleFilter === role ? 'primary' : 'default'}
              onClick={() => setRoleFilter(role)}
              icon={<Iconify icon="mdi:account" />}
            />
          );
        })}
      </Stack>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name or email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1 }}
      />

      <Stack
        sx={{ mt: 1, mb: 2 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {/* Select/Deselect All */}
        <Button size="small" onClick={handleSelectAll}>
          {allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredUsers.length})
        </Button>

        <Typography variant="caption" color="text.secondary">
          {selectedUsers.length} user(s) selected{' '}
          {roleFilter !== 'all' || debouncedSearch ? `from ${filteredUsers.length} filtered` : ''}
        </Typography>
      </Stack>

      {/* Virtualized List */}
      <Box sx={{ height: 400 }}>
        <List height={400} itemCount={filteredUsers.length} itemSize={60} width="100%">
          {({ index, style }) => {
            const user = filteredUsers[index];
            const isSelected = selectedSet.has(user.Id);
            return (
              <Box style={style} key={user.Id} sx={{ px: 1, py: 0.5 }}>
                <MemorizedUserChip
                  user={user}
                  isSelected={isSelected}
                  onClick={() => handleUserToggle(user.Id)}
                />
              </Box>
            );
          }}
        </List>
      </Box>
    </Box>
  );
};
